import React from 'react';
import { LL37Diagram } from '../assets/diagrams/LL37Diagram';
import { ThymosinAlpha1Diagram } from '../assets/diagrams/ThymosinAlpha1Diagram';
import { IpamorelinDiagram } from '../assets/diagrams/IpamorelinDiagram';
import { BPC157Diagram } from '../assets/diagrams/BPC157Diagram';
import { AOD9604Diagram } from '../assets/diagrams/AOD9604Diagram';

// Define the peptide data interface
export interface PeptideData {
  // Basic identification
  id: string;
  name: string;
  fullName: string;
  description: string;
  
  // Component data for PeptideModule
  componentData: {
    title: string;
    fullName: string;
    description: string[];
    diagram: React.ReactNode;
    keyTakeaway: string;
    mechanismsTable: {
      headers: string[];
      rows: {
        activity: string;
        activityColor: string;
        mechanism: string;
        clinicalRelevance: string;
      }[];
    };
    crossTalk: {
      title: string;
      content: string;
    };
    dosing: {
      title: string;
      protocols: {
        title: string;
        titleColor: string;
        steps: {
          number: number;
          label: string;
          content: string;
        }[];
      }[];
      additionalProtocol?: {
        title: string;
        titleColor: string;
        items: {
          number: number;
          label: string;
          content: string;
        }[][];
      };
    };
    storage: {
      title: string;
      items: string[];
    };
    benefits: {
      title: string;
      items: {
        title: string;
        content: string;
      }[];
    };
    additionalContent?: React.ReactNode;
  };
}

// Define categories
export const peptideCategories = [
  {
    id: 'immunomodulatory',
    name: 'Immunomodulatory Peptides',
    peptides: [
      {
        id: 'thymosin-alpha-1',
        name: 'Thymosin Alpha-1',
        fullName: 'Thymosin Alpha-1 (TA-1, Thymalfasin)',
        description: 'A thymic peptide that modulates immunity and inflammation, enhancing T-cell and dendritic cell function.'
      },
      {
        id: 'll-37',
        name: 'LL-37',
        fullName: 'LL-37 (Cathelicidin Antimicrobial Peptide)',
        description: 'A human host-defense peptide with antimicrobial, immunomodulatory, and wound healing properties.'
      }
    ]
  },
  {
    id: 'metabolic',
    name: 'Metabolic Peptides',
    peptides: [
      {
        id: 'ipamorelin',
        name: 'Ipamorelin',
        fullName: 'Ipamorelin (Growth Hormone Secretagogue)',
        description: 'A synthetic growth hormone secretagogue that mimics ghrelin, stimulating pulsatile growth hormone release without significantly affecting cortisol or prolactin levels.'
      }
    ]
  },
  {
    id: 'additional',
    name: 'Additional Peptides',
    peptides: [
      {
        id: 'bpc-157',
        name: 'BPC-157',
        fullName: 'BPC-157 (Body Protection Compound)',
        description: 'A gastric pentadecapeptide with powerful healing and regenerative properties for various tissues including GI tract, tendons, and muscles.'
      },
      {
        id: 'aod-9604',
        name: 'AOD-9604',
        fullName: 'AOD-9604 (Growth Hormone Fragment 176-191)',
        description: 'A modified fragment of human Growth Hormone with lipolytic (fat-burning) activity without GH\'s growth effects.'
      }
    ]
  }
];

// Define detailed data for each peptide

// Ipamorelin data
export const ipamorelinData: PeptideData = {
  id: 'ipamorelin',
  name: 'Ipamorelin',
  fullName: 'Ipamorelin (Growth Hormone Secretagogue)',
  description: 'A synthetic growth hormone secretagogue that mimics ghrelin, stimulating pulsatile growth hormone release without significantly affecting cortisol or prolactin levels.',
  componentData: {
    title: 'Ipamorelin',
    fullName: 'Ipamorelin (Growth Hormone Secretagogue)',
    description: [
      'Ipamorelin is a synthetic Growth Hormone Secretagogue (GHS) that mimics ghrelin. It binds to the ghrelin (GHSR-1a) receptor in the pituitary and hypothalamus, thereby stimulating pulsatile release of Growth Hormone (GH).',
      'Its mechanism is similar to older GHRPs (growth hormone–releasing peptides), but ipamorelin is highly selective for GH release. Crucially, it increases GH secretion without significantly raising cortisol or prolactin levels, distinguishing it from other GHRPs.',
      'Ipamorelin triggers the GH release pathway distinct from GHRH\'s receptor, yet it works additively with GHRH. Downstream, the GH surge leads to increased Insulin-like Growth Factor 1 (IGF-1) from the liver, mediating many anabolic effects. In peripheral tissues, ipamorelin\'s GH-driven actions enhance protein synthesis, lipolysis, and tissue repair.'
    ],
    diagram: <IpamorelinDiagram />,
    keyTakeaway: 'Ipamorelin stands out among Growth Hormone Secretagogues for its selectivity in promoting GH release without significantly affecting other hormones like cortisol or prolactin. This selective action makes it valuable for achieving the metabolic and regenerative benefits of increased GH/IGF-1 while minimizing unwanted side effects that can occur with less specific GHRPs.',
    mechanismsTable: {
      headers: ['Activity', 'Mechanism', 'Clinical Relevance'],
      rows: [
        {
          activity: 'GH Stimulation',
          activityColor: 'blue-600',
          mechanism: 'Binds to GHSR-1a receptors on somatotrophs, triggering selective GH release',
          clinicalRelevance: 'Increases IGF-1 production with minimal impact on other pituitary hormones'
        },
        {
          activity: 'Anabolic',
          activityColor: 'green-600',
          mechanism: 'GH/IGF-1 cascade enhances protein synthesis in muscle and other tissues',
          clinicalRelevance: 'Promotes muscle growth, recovery, and reduces sarcopenia in aging'
        },
        {
          activity: 'Lipolytic',
          activityColor: 'red-600',
          mechanism: 'GH activates hormone-sensitive lipase in adipocytes, increasing fatty acid release',
          clinicalRelevance: 'Reduces body fat, particularly visceral adiposity'
        },
        {
          activity: 'Tissue Repair',
          activityColor: 'purple-600',
          mechanism: 'GH/IGF-1 promotes cell proliferation and collagen synthesis',
          clinicalRelevance: 'Accelerates healing of injuries and may improve joint health'
        },
        {
          activity: 'Metabolic',
          activityColor: 'amber-600',
          mechanism: 'Alters glucose metabolism and insulin sensitivity',
          clinicalRelevance: 'May require glucose monitoring; affects energy substrate utilization'
        }
      ]
    },
    crossTalk: {
      title: 'Cross-talk with Other Pathways',
      content: 'While ipamorelin primarily targets the GH axis, it intersects with metabolic and endocrine pathways. By activating GH/IGF-1, it indirectly influences insulin sensitivity (GH can raise blood glucose by antagonizing insulin). However, unlike ghrelin or some GHRPs, ipamorelin has minimal impact on the HPA axis – studies showed virtually no ACTH or cortisol release even at high doses. This is a key difference: GHRP-2/6 cross-talk with adrenal (cortisol) and appetite (NPY) pathways, whereas ipamorelin is relatively specific to GH with minimal appetite stimulation. It still engages the ghrelin receptor in the hypothalamus, so a mild increase in appetite can occur, but much less than GHRP-6. Ipamorelin\'s GH surge will also feed back on somatostatin and GHRH in a physiological manner, preserving normal feedback loops.'
    },
    dosing: {
      title: 'Dosing Protocols',
      protocols: [
        {
          title: 'Standard Protocol',
          titleColor: 'indigo',
          steps: [
            {
              number: 1,
              label: 'Dose',
              content: '200–300 mcg per injection'
            },
            {
              number: 2,
              label: 'Frequency',
              content: '1-3 times daily, subcutaneous'
            },
            {
              number: 3,
              label: 'Timing',
              content: 'Before bed and/or post-workout'
            },
            {
              number: 4,
              label: 'Cycle',
              content: '5 days on, 2 days off to avoid desensitization'
            }
          ]
        },
        {
          title: 'Anti-Aging Protocol',
          titleColor: 'purple',
          steps: [
            {
              number: 1,
              label: 'Combination',
              content: 'Ipamorelin 100 mcg + CJC-1295 (no DAC) 100 mcg'
            },
            {
              number: 2,
              label: 'Frequency',
              content: 'Once daily, typically before bedtime'
            },
            {
              number: 3,
              label: 'Duration',
              content: '3-6 months, with periodic IGF-1 monitoring'
            },
            {
              number: 4,
              label: 'Note',
              content: 'Best taken on empty stomach; avoid carbs for 1-2 hours'
            }
          ]
        }
      ],
      additionalProtocol: {
        title: 'Recovery/Rehabilitation Protocol',
        titleColor: 'blue',
        items: [
          [
            {
              number: 1,
              label: 'Dose',
              content: '300 mcg Ipamorelin'
            },
            {
              number: 2,
              label: 'Timing',
              content: 'Morning dose and before bed'
            }
          ],
          [
            {
              number: 3,
              label: 'Combination',
              content: 'Often used with BPC-157 or TB-500 for injury recovery'
            },
            {
              number: 4,
              label: 'Duration',
              content: '4-8 weeks, depending on healing requirements'
            }
          ]
        ]
      }
    },
    storage: {
      title: 'Storage and Handling Considerations',
      items: [
        'Lyophilized (powder) form should be stored at -20°C',
        'Reconstituted peptide should be refrigerated at 2-8°C',
        'Use within 30 days after reconstitution',
        'Protect from direct light exposure',
        'Reconstitute with bacteriostatic water (0.9% benzyl alcohol)'
      ]
    },
    benefits: {
      title: 'Key Therapeutic Benefits',
      items: [
        {
          title: 'Body Composition',
          content: 'Increases lean muscle mass and reduces body fat, particularly visceral adiposity'
        },
        {
          title: 'Recovery Enhancement',
          content: 'Accelerates healing from injuries and improves post-exercise recovery'
        },
        {
          title: 'Sleep Quality',
          content: 'May improve deep sleep when administered before bedtime'
        },
        {
          title: 'Gastric Motility',
          content: 'Shows potential for treating gastroparesis by activating ghrelin receptors in the gut'
        },
        {
          title: 'Anti-Aging Effects',
          content: 'May improve energy, exercise capacity, and bone density in older adults'
        }
      ]
    }
  }
};

// LL-37 data
export const ll37Data: PeptideData = {
  id: 'll-37',
  name: 'LL-37',
  fullName: 'LL-37 (Cathelicidin Antimicrobial Peptide)',
  description: 'A human host-defense peptide with antimicrobial, immunomodulatory, and wound healing properties.',
  componentData: {
    title: 'LL-37 (Cathelicidin)',
    fullName: 'LL-37 (Cathelicidin Antimicrobial Peptide)',
    description: [
      'LL-37 is a human host-defense peptide with dual antimicrobial action and immunomodulatory functions. As the only human cathelicidin, this 37-amino acid peptide plays a critical role in innate immunity and wound healing.',
      'Its primary mechanisms include directly disrupting bacterial membranes and biofilms, helping to eliminate pathogens that may be resistant to conventional antibiotics. Uniquely, LL-37 also promotes tissue repair and wound healing by activating formyl peptide receptors (e.g., FPR2/FPRL1) on cells, leading to enhanced cell migration and re-epithelialization.',
      'LL-37 further influences inflammation by binding bacterial lipopolysaccharides (LPS), preventing excessive cytokine release during infections, while also inducing angiogenesis (new blood vessel formation) critical for tissue repair.'
    ],
    diagram: <LL37Diagram />,
    keyTakeaway: 'LL-37 is unique among peptides for its dual antimicrobial and wound-healing properties. Its ability to directly kill bacteria (including resistant strains) while simultaneously promoting tissue repair makes it especially valuable for infected chronic wounds. The peptide bridges innate immunity and regenerative pathways, functioning at the intersection of infection control and tissue healing.',
    mechanismsTable: {
      headers: ['Activity', 'Mechanism', 'Clinical Relevance'],
      rows: [
        {
          activity: 'Antimicrobial',
          activityColor: 'red-600',
          mechanism: 'Direct membranolytic action on bacteria; disrupts bacterial membrane integrity',
          clinicalRelevance: 'Effective against both Gram+ and Gram- bacteria, even antibiotic-resistant strains'
        },
        {
          activity: 'Anti-biofilm',
          activityColor: 'orange-600',
          mechanism: 'Prevents bacterial attachment; alters bacterial gene expression and quorum sensing',
          clinicalRelevance: 'Overcomes biofilm-associated resistance in chronic infections and wounds'
        },
        {
          activity: 'Wound Healing',
          activityColor: 'green-600',
          mechanism: 'Activates keratinocyte migration via FPR2/ILK signaling; stimulates re-epithelialization',
          clinicalRelevance: 'Accelerates closure of chronic wounds and ulcers'
        },
        {
          activity: 'Angiogenesis',
          activityColor: 'blue-600',
          mechanism: 'Upregulates HIF-1α and VEGF; acts via FPRL1 to induce new blood vessel formation',
          clinicalRelevance: 'Improves blood supply to healing tissue, crucial for ischemic wounds'
        },
        {
          activity: 'Immunomodulation',
          activityColor: 'purple-600',
          mechanism: 'Binds LPS to prevent excessive TNF-α/IL-1 release; context-dependent inflammation control',
          clinicalRelevance: 'Helps orchestrate balanced immune response without excessive inflammation'
        }
      ]
    },
    crossTalk: {
      title: 'Cross-talk with Other Pathways',
      content: "LL-37 sits at the intersection of antimicrobial defense and tissue regeneration. It interacts with immune receptors like FPR2 on neutrophils, macrophages, and lymphocytes to modulate inflammation. It also interferes with TLR signaling: by sequestering LPS and even self-DNA/RNA, LL-37 can alter TLR4 and TLR9 activation, balancing pro- and anti-inflammatory signals. In wound healing, LL-37's promotion of angiogenesis involves cross-talk with growth factor pathways, mimicking some effects of bFGF/VEGF. Additionally, vitamin D-VDR signaling cross-talks with LL-37 expression, with sufficient vitamin D required for optimal cathelicidin levels."
    },
    dosing: {
      title: 'Dosing Protocols',
      protocols: [
        {
          title: 'Topical Application',
          titleColor: 'indigo',
          steps: [
            {
              number: 1,
              label: 'Concentration',
              content: '0.5–1.6 mg/mL in gel formulation'
            },
            {
              number: 2,
              label: 'Frequency',
              content: 'Applied daily to wound bed'
            },
            {
              number: 3,
              label: 'Duration',
              content: '4-8 weeks, depending on wound response'
            },
            {
              number: 4,
              label: 'Caution',
              content: 'Higher concentrations (>3.2 mg/mL) may cause irritation'
            }
          ]
        },
        {
          title: 'Local Injection (Investigational)',
          titleColor: 'purple',
          steps: [
            {
              number: 1,
              label: 'Dose',
              content: '50–100 μg per injection site'
            },
            {
              number: 2,
              label: 'Technique',
              content: 'Intradermal or perilesional around chronic wounds'
            },
            {
              number: 3,
              label: 'Frequency',
              content: '1–3× weekly, guided by wound response'
            },
            {
              number: 4,
              label: 'Note',
              content: 'Research-use only; not routine clinical practice'
            }
          ]
        }
      ],
      additionalProtocol: {
        title: 'Endogenous Optimization Approach',
        titleColor: 'blue',
        items: [
          [
            {
              number: 1,
              label: 'Method',
              content: 'Vitamin D supplementation to enhance endogenous LL-37'
            },
            {
              number: 2,
              label: 'Mechanism',
              content: '1,25(OH)₂D stimulates cathelicidin gene expression'
            }
          ],
          [
            {
              number: 3,
              label: 'Monitoring',
              content: 'Check 25(OH)D levels in patients with chronic wounds'
            },
            {
              number: 4,
              label: 'Approach',
              content: 'Target vitamin D sufficiency (>30 ng/mL) to enhance host defense'
            }
          ]
        ]
      }
    },
    storage: {
      title: 'Storage and Handling Considerations',
      items: [
        'LL-37 is susceptible to proteolytic degradation in wound environments',
        'Incorporation into hydrogels or controlled-release formulations increases stability',
        'For topical preparations, refrigerate when not in use',
        'For research formulations, lyophilized peptide should be stored at -20°C',
        'Avoid repeated freeze-thaw cycles to maintain bioactivity'
      ]
    },
    benefits: {
      title: 'Key Therapeutic Benefits',
      items: [
        {
          title: 'Enhanced Wound Healing',
          content: 'Accelerates chronic wound closure, particularly in diabetic and venous ulcers'
        },
        {
          title: 'Biofilm Disruption',
          content: 'Breaks down bacterial biofilms and inhibits their formation'
        },
        {
          title: 'Antimicrobial Action',
          content: 'Direct killing of bacteria, including antibiotic-resistant strains'
        },
        {
          title: 'Anti-inflammatory',
          content: 'Reduces excessive inflammation while maintaining immune protection'
        },
        {
          title: 'Angiogenic Effects',
          content: 'Promotes new blood vessel formation in wounds with compromised circulation'
        }
      ]
    }
  }
};

// Thymosin Alpha-1 data
export const thymosinAlpha1Data: PeptideData = {
  id: 'thymosin-alpha-1',
  name: 'Thymosin Alpha-1',
  fullName: 'Thymosin Alpha-1 (TA-1, Thymalfasin)',
  description: 'A thymic peptide that modulates immunity and inflammation, enhancing T-cell and dendritic cell function.',
  componentData: {
    title: 'Thymosin Alpha-1',
    fullName: 'Thymosin Alpha-1 (TA-1, Thymalfasin)',
    description: [
      'Thymosin Alpha-1 (TA-1) is a 28-amino acid peptide originally isolated from thymus tissue that plays a crucial role in immune system modulation. As a key thymic hormone, TA-1 primarily enhances T-cell and dendritic cell function, promoting the production of Th1 cytokines (such as IL-2, IL-12, and IFN-γ) and activating cytotoxic lymphocytes.',
      'Beyond immune enhancement, TA-1 demonstrates direct anti-proliferative effects against tumor and viral cells. It interacts with toll-like receptors (TLRs) on immune cells to trigger responses and boosts antioxidant enzymes like superoxide dismutase and glutathione peroxidase to reduce oxidative stress.',
      'This dual mechanism of immune stimulation and anti-proliferation makes TA-1 a valuable therapeutic agent for a range of conditions, from chronic viral infections to cancer adjuvant therapy and immunodeficiency states.'
    ],
    diagram: <ThymosinAlpha1Diagram />,
    keyTakeaway: 'Thymosin Alpha-1 functions as a central immune modulator that bridges innate and adaptive immunity. Its ability to enhance T-cell function and dendritic cell activation, coupled with direct anti-proliferative effects, makes it particularly valuable for treating immunodeficiency states, chronic infections, and as an adjuvant in cancer therapy.',
    mechanismsTable: {
      headers: ['Pathway', 'Mechanism', 'Clinical Relevance'],
      rows: [
        {
          activity: 'TLR Signaling',
          activityColor: 'blue-600',
          mechanism: 'Activates TLR signaling in dendritic cells and macrophages, triggering innate immune responses',
          clinicalRelevance: 'Enhances pathogen recognition and antigen presentation'
        },
        {
          activity: 'Cytokine Induction',
          activityColor: 'purple-600',
          mechanism: 'Upregulates IL-2, IL-12, and IFN-γ production; enhances IL-2 receptor expression',
          clinicalRelevance: 'Amplifies T-cell proliferation and cytotoxic responses against viruses and tumors'
        },
        {
          activity: 'Antioxidant Defense',
          activityColor: 'green-600',
          mechanism: 'Boosts SOD and glutathione peroxidase activity',
          clinicalRelevance: 'Reduces oxidative stress and cellular damage during immune activation'
        },
        {
          activity: 'Direct Anti-proliferative',
          activityColor: 'red-600',
          mechanism: 'Directly inhibits tumor and viral cell growth',
          clinicalRelevance: 'Complementary mechanism to immune-mediated tumor/virus control'
        },
        {
          activity: 'Checkpoint Modulation',
          activityColor: 'amber-600',
          mechanism: 'Intersects with immune checkpoint pathways',
          clinicalRelevance: 'Potential to enhance effects of checkpoint inhibitor therapies in cancer'
        }
      ]
    },
    crossTalk: {
      title: 'Cross-talk with Other Immune Pathways',
      content: 'TA-1 acts as a crucial bridge between innate and adaptive immunity. Through TLR signaling in dendritic cells and macrophages, it triggers innate pathways that subsequently lead to T-cell activation. It upregulates IL-2 receptors on lymphocytes, enhancing IL-2 signaling and lymphocyte proliferation, while increased IFNs and IL-12 amplify antiviral and Th1 responses that further cross-talk with NK cell and B-cell activity.'
    },
    dosing: {
      title: 'Standard Dosing Protocols',
      protocols: [
        {
          title: 'Chronic Infections',
          titleColor: 'indigo',
          steps: [
            {
              number: 1,
              label: 'Dose',
              content: '0.8–1.6 mg subcutaneously'
            },
            {
              number: 2,
              label: 'Frequency',
              content: 'Twice weekly'
            },
            {
              number: 3,
              label: 'Duration',
              content: '6-12 weeks, reassess response'
            },
            {
              number: 4,
              label: 'Indications',
              content: 'Chronic hepatitis B, hepatitis C (with antivirals)'
            }
          ]
        },
        {
          title: 'Acute/Critical Illness',
          titleColor: 'purple',
          steps: [
            {
              number: 1,
              label: 'Dose',
              content: '1.6 mg subcutaneously'
            },
            {
              number: 2,
              label: 'Frequency',
              content: 'Twice daily for 5 days, then daily'
            },
            {
              number: 3,
              label: 'Total Daily',
              content: '1.6–3.2 mg/day'
            },
            {
              number: 4,
              label: 'Indications',
              content: 'Sepsis, severe infections'
            }
          ]
        }
      ],
      additionalProtocol: {
        title: 'Cancer Adjuvant Therapy',
        titleColor: 'blue',
        items: [
          [
            {
              number: 1,
              label: 'Dose',
              content: '1.6 mg subcutaneously'
            },
            {
              number: 2,
              label: 'Frequency',
              content: 'Twice weekly'
            }
          ],
          [
            {
              number: 3,
              label: 'Duration',
              content: 'Alongside and after primary therapy'
            },
            {
              number: 4,
              label: 'Indications',
              content: 'NSCLC, melanoma, hepatocellular carcinoma'
            }
          ]
        ]
      }
    },
    storage: {
      title: 'Storage and Handling Information',
      items: [
        'Lyophilized peptide should be stored at -20°C',
        'Reconstituted solution should be refrigerated (2-8°C) and used within 7 days',
        'Avoid repeated freeze-thaw cycles',
        'Protect from light and maintain sterility when reconstituted',
        'Reconstitute with bacteriostatic water for injection (0.9% benzyl alcohol)'
      ]
    },
    benefits: {
      title: 'Key Therapeutic Benefits',
      items: [
        {
          title: 'Immune Restoration',
          content: 'Reverses immunosuppression in various clinical contexts'
        },
        {
          title: 'Viral Clearance',
          content: 'Improved clearance in hepatitis B and C when combined with antivirals'
        },
        {
          title: 'Sepsis Outcomes',
          content: 'Associated with 9% absolute reduction in mortality'
        },
        {
          title: 'Cancer Responses',
          content: 'Improved tumor response rates and survival in several cancer types'
        },
        {
          title: 'Post-COVID Recovery',
          content: 'Emerging evidence for benefit in long COVID and post-viral fatigue'
        }
      ]
    }
  }
};

// BPC-157 data
export const bpc157Data: PeptideData = {
  id: 'bpc-157',
  name: 'BPC-157',
  fullName: 'BPC-157 (Body Protection Compound)',
  description: 'A gastric pentadecapeptide with powerful healing and regenerative properties for various tissues including GI tract, tendons, and muscles.',
  componentData: {
    title: 'BPC-157',
    fullName: 'BPC-157 (Body Protection Compound)',
    description: [
      'BPC-157 is a pentadecapeptide (15 amino acid fragment) derived from Body Protection Compound (BPC), a naturally occurring protein found in gastric juice. It demonstrates remarkable healing properties across diverse tissue types.',
      'Its primary mechanisms involve promoting angiogenesis (formation of new blood vessels), accelerating growth factor expression, and enhancing the healing of various tissues including tendons, muscles, nerves, and the gastrointestinal tract.',
      'BPC-157 works through multiple pathways, including VEGF (Vascular Endothelial Growth Factor), growth hormone, and nitric oxide signaling systems. It also stabilizes cell membranes, protects endothelial tissue, and promotes tissue regeneration even in chronically damaged areas.'
    ],
    diagram: <BPC157Diagram />,
    keyTakeaway: 'BPC-157 is unique among peptides for its versatility in healing multiple tissue types while maintaining an exceptional safety profile. Rather than targeting a single pathway, it modulates several healing mechanisms simultaneously, making it effective for both acute injuries and chronic conditions across muscular, tendinous, nervous, and gastrointestinal tissues.',
    mechanismsTable: {
      headers: ['Activity', 'Mechanism', 'Clinical Relevance'],
      rows: [
        {
          activity: 'Angiogenic',
          activityColor: 'green-600',
          mechanism: 'Upregulates VEGF and stimulates formation of new blood vessels',
          clinicalRelevance: 'Accelerates healing in poorly vascularized tissues like tendons'
        },
        {
          activity: 'GI Protective',
          activityColor: 'red-600',
          mechanism: 'Preserves gastric mucosal integrity and counteracts various ulcerogenic insults',
          clinicalRelevance: 'May benefit inflammatory bowel conditions and prevent NSAID-induced damage'
        },
        {
          activity: 'Tendon Healing',
          activityColor: 'blue-600',
          mechanism: 'Increases type I collagen production and fibroblast activity',
          clinicalRelevance: 'Enhances recovery from tendon injuries that typically heal slowly'
        },
        {
          activity: 'Neuroprotective',
          activityColor: 'purple-600',
          mechanism: 'Influences dopaminergic and GABAergic systems; reduces excitotoxicity',
          clinicalRelevance: 'Shows potential for peripheral nerve injuries and neuropathic conditions'
        },
        {
          activity: 'Anti-inflammatory',
          activityColor: 'amber-600',
          mechanism: 'Modulates nitric oxide production and stabilizes cell membranes',
          clinicalRelevance: 'Reduces inflammatory markers without immunosuppression'
        }
      ]
    },
    crossTalk: {
      title: 'Cross-talk with Other Pathways',
      content: 'BPC-157 demonstrates remarkable pathway integration, particularly between growth factors and healing mechanisms. It interacts with the nitric oxide (NO) system, promoting vasodilation while preventing NO system disruption. This interaction helps explain its gastric healing effects. The peptide also influences growth factor expression, enhancing EGF (Epidermal Growth Factor) and FGF (Fibroblast Growth Factor) activity, which creates a favorable environment for tissue repair. In tendon healing, BPC-157 increases the activity of both tendon fibroblasts and growth factors while improving blood flow — creating a multi-pathway approach to healing. The peptide also interacts with the dopamine system, which may explain some of its neuroprotective qualities, while simultaneously modulating inflammatory responses without suppressing immune function.'
    },
    dosing: {
      title: 'Dosing Protocols',
      protocols: [
        {
          title: 'Standard Protocol',
          titleColor: 'indigo',
          steps: [
            {
              number: 1,
              label: 'Dose',
              content: '250-500 mcg per administration'
            },
            {
              number: 2,
              label: 'Frequency',
              content: 'Once or twice daily'
            },
            {
              number: 3,
              label: 'Route',
              content: 'Subcutaneous injection or oral administration'
            },
            {
              number: 4,
              label: 'Duration',
              content: '2-4 weeks for acute injuries; 4-8 weeks for chronic conditions'
            }
          ]
        },
        {
          title: 'Localized Injury Protocol',
          titleColor: 'emerald',
          steps: [
            {
              number: 1,
              label: 'Dose',
              content: '250 mcg per administration'
            },
            {
              number: 2,
              label: 'Frequency',
              content: 'Twice daily'
            },
            {
              number: 3,
              label: 'Administration',
              content: 'Subcutaneous injection near injury site'
            },
            {
              number: 4,
              label: 'Duration',
              content: 'Daily for 2-4 weeks, assess progress and extend if needed'
            }
          ]
        }
      ],
      additionalProtocol: {
        title: 'GI-Specific Protocol',
        titleColor: 'orange',
        items: [
          [
            {
              number: 1,
              label: 'Dose',
              content: '500 mcg BPC-157'
            },
            {
              number: 2,
              label: 'Route',
              content: 'Oral administration (stable form)'
            }
          ],
          [
            {
              number: 3,
              label: 'Timing',
              content: 'Morning and evening, away from meals'
            },
            {
              number: 4,
              label: 'Duration',
              content: '4-8 weeks for conditions like IBD or GERD'
            }
          ]
        ]
      }
    },
    storage: {
      title: 'Storage and Handling Considerations',
      items: [
        'Store lyophilized (powder) form at -20°C',
        'Reconstituted peptide should be refrigerated at 2-8°C',
        'Use within 2-3 weeks after reconstitution',
        'Protect from light and repeated freeze-thaw cycles',
        'Stable oral forms available for digestive system applications'
      ]
    },
    benefits: {
      title: 'Key Therapeutic Benefits',
      items: [
        {
          title: 'Musculoskeletal Healing',
          content: 'Accelerates recovery from tendon, ligament, and muscle injuries with improved tissue quality'
        },
        {
          title: 'Digestive System',
          content: 'Helps heal leaky gut, inflammatory bowel conditions, and protects against NSAID damage'
        },
        {
          title: 'Wound Healing',
          content: 'Enhances skin wound closure and improves healing quality with reduced scarring'
        },
        {
          title: 'Joint Health',
          content: 'Relieves joint pain and improves mobility in both acute injuries and chronic conditions'
        },
        {
          title: 'Neuroprotection',
          content: 'Shows potential for peripheral nerve injury recovery and certain central nervous system applications'
        }
      ]
    }
  }
};

// AOD-9604 data
export const aod9604Data: PeptideData = {
  id: 'aod-9604',
  name: 'AOD-9604',
  fullName: 'AOD-9604 (Growth Hormone Fragment 176-191)',
  description: 'A modified fragment of human Growth Hormone with lipolytic (fat-burning) activity without GH\'s growth effects.',
  componentData: {
    title: 'AOD-9604',
    fullName: 'AOD-9604 (Growth Hormone Fragment 176-191)',
    description: [
      'AOD-9604 is a modified C-terminal fragment of human Growth Hormone (GH), specifically amino acids 176-191. Originally developed as an anti-obesity drug, it retains the fat-reducing effects of GH without stimulating IGF-1 production or impacting growth.',
      'This peptide primarily works by stimulating lipolysis (breakdown of stored fat) while inhibiting lipogenesis (formation of new fat). Its mechanism involves β3-adrenergic receptor activation in adipocytes, triggering a cascade that increases intracellular cAMP and activates hormone-sensitive lipase.',
      'Unlike full-length GH, AOD-9604 has minimal impact on blood glucose and doesn\'t stimulate IGF-1 production, potentially offering safer metabolic benefits. Some research also suggests potential chondroprotective effects (cartilage protection), though this mechanism requires further study.'
    ],
    diagram: <AOD9604Diagram />,
    keyTakeaway: 'AOD-9604 represents a targeted approach to fat metabolism, harnessing GH\'s lipolytic benefits while avoiding its growth-promoting and diabetogenic effects. Its selective mechanism on fat mobilization through β3-adrenergic pathways makes it a potential tool for addressing localized adiposity while minimizing systemic metabolic impact.',
    mechanismsTable: {
      headers: ['Activity', 'Mechanism', 'Clinical Relevance'],
      rows: [
        {
          activity: 'Lipolytic',
          activityColor: 'purple-600',
          mechanism: 'Activates β3-adrenergic receptors in adipocytes; increases cAMP and activates hormone-sensitive lipase',
          clinicalRelevance: 'Enhances breakdown of stored triglycerides into free fatty acids and glycerol'
        },
        {
          activity: 'Anti-Lipogenic',
          activityColor: 'blue-600',
          mechanism: 'Inhibits acetyl-CoA carboxylase, reducing fatty acid synthesis pathways',
          clinicalRelevance: 'Decreases formation of new fat cells and lipid accumulation'
        },
        {
          activity: 'Metabolic Regulation',
          activityColor: 'green-600',
          mechanism: 'Increases energy expenditure through uncoupling proteins in adipose tissue',
          clinicalRelevance: 'May enhance fat utilization without significantly affecting muscle mass'
        },
        {
          activity: 'Chondroprotective',
          activityColor: 'amber-600',
          mechanism: 'Potential stimulation of cartilage formation and protection (mechanism not fully elucidated)',
          clinicalRelevance: 'Preliminary evidence suggests possible benefit for joint health'
        },
        {
          activity: 'Insulin-Sparing',
          activityColor: 'red-600',
          mechanism: 'Unlike full GH, minimal impact on glucose metabolism and insulin sensitivity',
          clinicalRelevance: 'Less likely to promote insulin resistance compared to intact GH'
        }
      ]
    },
    crossTalk: {
      title: 'Cross-talk with Other Pathways',
      content: 'AOD-9604 operates primarily through fat metabolism pathways but exhibits important cross-talk with several systems. Unlike full GH, it shows minimal interaction with the GH/IGF-1 axis, which is a key distinction from intact growth hormone. While it stimulates β3-adrenergic pathways, it doesn\'t significantly affect the α-adrenergic system that controls vasculature and blood pressure. There may be indirect interaction with thyroid hormone pathways as both systems influence metabolic rate and thermogenesis. AOD-9604 appears to complement rather than antagonize leptin signaling in regulating energy balance. The peptide has shown some potential interaction with cartilage metabolism pathways, suggesting cross-talk with chondrocyte function and possibly the Wnt/β-catenin pathway involved in cartilage homeostasis, though these mechanisms require further research.'
    },
    dosing: {
      title: 'Dosing Protocols',
      protocols: [
        {
          title: 'Standard Protocol',
          titleColor: 'indigo',
          steps: [
            {
              number: 1,
              label: 'Dose',
              content: '300-500 mcg per injection'
            },
            {
              number: 2,
              label: 'Frequency',
              content: 'Once daily, subcutaneous injection'
            },
            {
              number: 3,
              label: 'Timing',
              content: 'Preferably in the morning on empty stomach'
            },
            {
              number: 4,
              label: 'Duration',
              content: '8-12 weeks cycle, followed by 4-week break'
            }
          ]
        },
        {
          title: 'Body Composition Protocol',
          titleColor: 'purple',
          steps: [
            {
              number: 1,
              label: 'Dose',
              content: '400 mcg AOD-9604'
            },
            {
              number: 2,
              label: 'Frequency',
              content: 'Daily injection before morning fasting cardio'
            },
            {
              number: 3,
              label: 'Companion',
              content: 'Often combined with 30-minute fasted exercise'
            },
            {
              number: 4,
              label: 'Duration',
              content: '12 weeks with body composition assessment every 4 weeks'
            }
          ]
        }
      ],
      additionalProtocol: {
        title: 'Local Application Protocol',
        titleColor: 'blue',
        items: [
          [
            {
              number: 1,
              label: 'Method',
              content: 'Mesotherapy or shallow subcutaneous injection'
            },
            {
              number: 2,
              label: 'Dosing',
              content: '250-300 mcg divided into multiple small injections'
            }
          ],
          [
            {
              number: 3,
              label: 'Target',
              content: 'Localized to specific adipose regions'
            },
            {
              number: 4,
              label: 'Frequency',
              content: '2-3 times weekly for 6-8 weeks'
            }
          ]
        ]
      }
    },
    storage: {
      title: 'Storage and Handling Considerations',
      items: [
        'Lyophilized powder should be stored at -20°C',
        'Reconstituted peptide stable for up to 21 days when refrigerated (2-8°C)',
        'Reconstitute with bacteriostatic water (0.9% benzyl alcohol)',
        'Protect from direct light and avoid repeated freeze-thaw cycles',
        'Do not use if solution appears cloudy or contains particles'
      ]
    },
    benefits: {
      title: 'Key Therapeutic Benefits',
      items: [
        {
          title: 'Fat Reduction',
          content: 'Enhances breakdown of stored fat and may help reduce stubborn adipose tissue'
        },
        {
          title: 'Metabolic Health',
          content: 'May improve lipid profile and body composition without significant insulin resistance effects'
        },
        {
          title: 'Joint Support',
          content: 'Preliminary evidence suggests potential benefit for cartilage health and joint function'
        },
        {
          title: 'Targeted Approach',
          content: 'Offers fat-mobilizing effects of GH without systemic growth promotion or IGF-1 increase'
        },
        {
          title: 'Athletic Recovery',
          content: 'Some users report improved recovery from exercise-induced soreness and joint discomfort'
        }
      ]
    }
  }
};

// Map of all peptides for easy access
export const peptideDataMap: { [key: string]: PeptideData } = {
  'll-37': ll37Data,
  'thymosin-alpha-1': thymosinAlpha1Data,
  'ipamorelin': ipamorelinData,
  'bpc-157': bpc157Data,
  'aod-9604': aod9604Data
};